import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { switchMap } from 'rxjs/operators';
import { DriverTask } from 'src/app/classes/driver-task';
import { Profile } from 'src/app/classes/profile';
import { TerminalRunLocation } from 'src/app/classes/terminal-run-location';
import { TerminalRunTask } from 'src/app/classes/terminal-run-task';
import { TerminalRunUpdateTimePayload } from 'src/app/classes/terminal-run-update-time-payload';
import { IncDecEnum, PickDropEnum, TerminalRunTypeEnum, TerminalTask } from 'src/app/classes/terminal-task';
import { EventsDeltaService } from 'src/app/services/events-delta.service';
import { EventsService } from 'src/app/services/events.service';
import { MyTasksService } from 'src/app/services/my-tasks.service';
import { ProfileService } from 'src/app/services/profile.service';
import { TerminalRunsService } from 'src/app/services/terminal-runs.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilitiesService } from 'src/app/services/utilities.service';

const HH_MM_CTRL_OPTS = /^([01]{0,1}[0-9]|2[0-3]):[0-5][0-9]$/;

@Component({
  standalone: false,
  selector: 'app-terminal-run-details',
  templateUrl: './terminal-run-details.component.html',
  styleUrls: [ './terminal-run-details.component.scss' ]
})
export class TerminalRunDetailsComponent implements OnInit {
  @Input() task: TerminalRunTask | DriverTask;
  @Input() mode: 'Default' | 'Dispatch' | 'MyTasks' | 'UnassignedTasks' = 'Default';
  @Output() updateTask = new EventEmitter<any>();
  @Output() deleteTask = new EventEmitter<any>();
  @Output() endTask = new EventEmitter<any>();
  @Output() unassignTask = new EventEmitter<any>();

  loadPickup = false;
  loadDropoff = false;

  coordNotesPickupCtrl: FormControl = this.fb.control('');
  driverNotesPickupCtrl: FormControl = this.fb.control('');

  coordNotesDropoffCtrl: FormControl = this.fb.control('');
  driverNotesDropoffCtrl: FormControl = this.fb.control('');

  INC_DEC = IncDecEnum;
  TERMINAL_RUN_TYPE = TerminalRunTypeEnum;
  PICK_DROP = PickDropEnum;

  constructor(
    readonly fb: FormBuilder,
    readonly utilityService: UtilitiesService,
    readonly profileService: ProfileService,
    readonly terminalRunsService: TerminalRunsService,
    readonly myTasksService: MyTasksService,
    readonly dialog: MatDialog,
    private eventDeltaService: EventsDeltaService,
    private eventsService: EventsService,
    readonly toastService: ToastService
  ) {}

  getDriverNotes(pickDrop: string, pickDropNotes: any): string {
    return pickDrop === PickDropEnum.pickup ? pickDropNotes.driverNotesPickup : pickDropNotes.driverNotesDropoff;
  }

  getCoordNotes(pickDrop: string, pickDropNotes: any): string {
    return pickDrop === PickDropEnum.pickup ? pickDropNotes.coordNotesPickup : pickDropNotes.coordNotesDropoff;
  }

  ngOnInit(): void {
    if (this.mode === 'MyTasks') {
      this.coordNotesPickupCtrl.disable();
      this.coordNotesDropoffCtrl.disable();
    }
    if (this.task.taskName.includes('Terminal') && (this.mode === 'MyTasks' || this.mode === 'Dispatch' || this.mode === 'Default')) {
      this.coordNotesPickupCtrl.setValue(this.task.pickupNotes.coordNotesPickup);
      this.driverNotesPickupCtrl.setValue(this.task.pickupNotes.driverNotesPickup);
      this.coordNotesDropoffCtrl.setValue(this.task.dropoffNotes.coordNotesDropoff);
      this.driverNotesDropoffCtrl.setValue(this.task.dropoffNotes.driverNotesDropoff);
    }
  }

  pickDropPassengers(
    location: TerminalRunLocation,
    incOrDec: IncDecEnum,
    type: TerminalRunTypeEnum,
    pickDrop: PickDropEnum
  ): any {
    this.profileService
      .getLatestProfileOnce()
      .pipe(
        switchMap((profile: Profile) => {
          const payload: TerminalTask = {
            increaseOrDecrease: incOrDec,
            location: location.location,
            numVal:
              incOrDec === IncDecEnum.increase
                ? +location.passengerCount + 1
                : Math.max(0, +location.passengerCount - 1),
            pickupOrDropoff: pickDrop,
            taskName: this.task.taskName,
            time: moment().format(),
            type: type,
            updatedBy: profile.adId
          };
          return this.terminalRunsService.terminalTask(payload);
        })
      )
      .subscribe(() => this.updateTask.emit());
  }

  confirmPickupDropOff(location: TerminalRunLocation, pickDrop: PickDropEnum): void {
    console.log('confirm pickup');
    this.togglePickupDropOff(location, TerminalRunTypeEnum.terminalRunConfirmWork, pickDrop);
  }

  revertConfirmPickupDropOff(location: TerminalRunLocation, pickDrop: PickDropEnum): void {
    this.togglePickupDropOff(location, TerminalRunTypeEnum.terminalRunRevertConfirmWork, pickDrop);
  }

  private togglePickupDropOff(location: TerminalRunLocation, type: TerminalRunTypeEnum, pickDrop: PickDropEnum): void {
    this.profileService
      .getLatestProfileOnce()
      .pipe(
        switchMap((profile: Profile) => {
          const payload: TerminalTask = {
            location: location.location,
            pickupOrDropoff: pickDrop,
            taskName: this.task.taskName,
            time: moment().format(),
            type: type,
            updatedBy: profile.adId
          };
          return this.terminalRunsService.terminalTask(payload);
        })
      )
      .subscribe(() => this.updateTask.emit());
  }

  updateCoordinatorNotes(pickDrop: PickDropEnum, value: any): void {
    this.notesUpdated(value, TerminalRunTypeEnum.terminalUpdateCoordNote, pickDrop);
  }

  updateDriverNotes(pickDrop: PickDropEnum, value: any): void {
    this.notesUpdated(value, TerminalRunTypeEnum.terminalUpdateDriverNote, pickDrop);
  }

  private notesUpdated(note: string, taskType: TerminalRunTypeEnum, pickDrop: PickDropEnum): void {
    this.profileService
      .getLatestProfileOnce()
      .pipe(
        switchMap((profile: Profile) => {
          const payload: TerminalTask = {
            note: (note && note.trim()) || '',
            noteType: pickDrop,
            taskName: this.task.taskName,
            type: taskType,
            updatedBy: profile.adId
          };
          return this.terminalRunsService.terminalTask(payload);
        })
      )
      .subscribe(() => this.updateTask.emit());
  }

  assignTerminalTask(task: TerminalRunTask): void {
    const adId = this.profileService.profileObs$.getValue().adId;
    if (!this.eventsService.driverHasVan(adId)) {
      this.toastService.show('You do not have a van assigned. Please get a van before assign tasks.', { classname: 'bg-danger text-light', delay: 8000 });
      return;
    }
    this.profileService
      .getLatestProfileOnce()
      .pipe(
        switchMap((profile: Profile) => {
          const payload: any = {
            taskIds: [ task.taskName ],
            assignTo: profile.adId,
            type: 'assign'
          };
          return this.terminalRunsService.assignTask(payload);
        })
      )
      .subscribe(() => {
        this.updateTask.emit()
        this.eventDeltaService.getDeltas();
      });
  }

  canDeleteTask(task: TerminalRunTask | DriverTask): boolean {
    return this.canUnassignTask(task) && this.taskCreatedByDriver(task as DriverTask);
  }

  canEndTask(task: TerminalRunTask | DriverTask): boolean {
    return this.mode === 'MyTasks' && (task as DriverTask).actualStart && this.taskCreatedByDriver(task as DriverTask);
  }

  canUnassignTask(task: TerminalRunTask | DriverTask): boolean {
    return this.mode === 'MyTasks' && !(task as DriverTask).actualStart;
  }

  taskCreatedByDriver(task: DriverTask): boolean {
    return this.profileService.profileObs$.getValue().adId.trim().toUpperCase() === task.createdBy.trim().toUpperCase();
  }

  updateTerminalTime(location: any, value: any, pickupOrDropoff: string, taskName: string): void{
    if(!HH_MM_CTRL_OPTS.test(value)){
      this.toastService.show('Invalid time format, data was not submitted! The Correct format is \'HH:MM\'', {
        classname: 'bg-danger text-light',
        delay: 4000
      });
    }else{
      if (pickupOrDropoff === 'pickup') {
        this.loadPickup = true;
      } else {
        this.loadDropoff = true;
      }
      const payload: TerminalRunUpdateTimePayload = {
        location: location.location,
        pickupOrDropoff: pickupOrDropoff,
        taskName: taskName,
        time: value,
        type: 'dispatchTerminalRunConfirmWork',
        updatedBy: this.profileService.profileObs$.getValue().adId
      }
      this.terminalRunsService.terminalTaskTimeUpdate(payload).subscribe((resp) => {
        if(resp.status === 200){
          this.toastService.show(`Time Updated`, { classname: 'bg-success text-light', delay: 2000 });
        }else{
          this.toastService.show(`Error ${resp.status} occurred updating time`, { classname: 'bg-danger text-light', delay: 4000 });
        }
        this.loadPickup = false;
        this.loadDropoff = false;
      });
    }
  }

  extractTime(value): string{
    if(value && moment(value).isValid()){
      return moment(value).format('HH:mm');
    }
    return null;
  }
}
